import { FC } from "react";
import { Box, Typography } from "@mui/material";
import {
  GUARDRAIL_ROLLING_OPTION_LABELS,
  sortGuardrailRollingOptions,
  type GuardrailRollingOption,
  type TimeSeriesViewerOptions,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
import HPButton from "@/components/common/Button";
import { TREND_UNIT_LABELS, PIT_PERIOD_LABELS } from "@utils/tab-time-labels";
import {
  summarizeSharedContract,
  getTabTimeAdoption,
  viewerContractKey,
  type TabTimeFamilyKind,
} from "@utils/tab-time-controls";
import { useTabTimeAlignment } from "@hooks/use-tab-time-alignment";

interface TabTimeAlignmentCalloutProps {
  kind: TabTimeFamilyKind;
  datasetId: string | null;
  /** The chart's saved time unit (trend) or named period (point-in-time). */
  savedSetting: string | null;
  /** The chart's saved rolling average (kept as-is when adopting the contract). */
  savedRA: GuardrailRollingOption;
  /** The chart's current viewer-time options — used to tell if adopting is a no-op. */
  currentViewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions;
  /**
   * Whether the in-progress edit breaks an existing alignment. Computed once by
   * the parent section (shared with the break warning). When true, the break
   * warning owns the "Restore shared configuration" action (a superset of
   * adopting the contract), so this callout's adopt action is suppressed to
   * avoid two overlapping controls.
   */
  breaksAlignment: boolean;
  /** Apply the adopted viewer-time contract to the chart being edited. */
  onApply: (
    viewerOptions: TimeSeriesViewerOptions | PointInTimeViewerOptions,
  ) => void;
}

const chartsNoun = (count: number, kind: TabTimeFamilyKind): string => {
  const family = kind === "trend" ? "trend" : "point-in-time";
  return `${count} other ${family} chart${count === 1 ? "" : "s"}`;
};

/**
 * Suggestion callout for the Viewer Time Options editor section. When the other
 * visualizations in the same family + dataset on the tab already agree on one
 * tab-time configuration, it summarizes that shared config; when they disagree,
 * it shows a neutral "configurations vary" note. Read-only — no action yet.
 */
export const TabTimeAlignmentCallout: FC<TabTimeAlignmentCalloutProps> = ({
  kind,
  datasetId,
  savedSetting,
  savedRA,
  currentViewerOptions,
  breaksAlignment,
  onApply,
}) => {
  const alignment = useTabTimeAlignment(kind, datasetId);
  if (alignment.status === "none") return null;

  const labelFor = (value: string): string =>
    (kind === "trend" ? TREND_UNIT_LABELS : PIT_PERIOD_LABELS)[value] ?? value;

  if (alignment.status === "varies") {
    return (
      <Box
        sx={{
          borderRadius: 1,
          border: "1px solid #E4E7EC",
          backgroundColor: "#F9FAFB",
          px: 1.5,
          py: 1.25,
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#475467" }}>
          Tab time configurations vary across this tab.
        </Typography>
      </Box>
    );
  }

  const { count, config } = alignment;
  const summary = summarizeSharedContract(config, kind);
  const primaryNoun = kind === "trend" ? "time unit" : "time period";
  const adoption = getTabTimeAdoption(config, kind, savedSetting, savedRA);

  // The chart already exposes the shared contract — adopting it would be a no-op,
  // so offer a confirmation instead of a dead button.
  const alreadyApplied =
    adoption.status === "ready" &&
    currentViewerOptions.enabled &&
    viewerContractKey(currentViewerOptions, kind) ===
      viewerContractKey(adoption.viewerOptions, kind);

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: "1px solid var(--color-hp-med-blue)",
        backgroundColor: "var(--color-hp-light-blue)",
        px: 1.5,
        py: 1.25,
        mb: 1.5,
      }}
    >
      <Typography
        sx={{ fontSize: 12, fontWeight: 600, color: "var(--color-hp-dark-blue)" }}
      >
        {chartsNoun(count, kind)} on this tab share a tab time configuration.
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#475467", mt: 0.5 }}>
        Default {primaryNoun}:{" "}
        <span className="font-medium text-[#344054]">
          {labelFor(config.savedDefault)}
        </span>
      </Typography>

      <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {summary.map((option) => (
          <Box
            key={option.value}
            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "baseline" }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 12, fontWeight: 500, color: "#344054" }}
            >
              {labelFor(option.value)}
              {option.isDefault && (
                <span className="ml-1 text-[10px] text-[#98A2B3]">(default)</span>
              )}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12, color: "#667085" }}>
              {sortGuardrailRollingOptions(option.rollingAverages)
                .map((ra) => GUARDRAIL_ROLLING_OPTION_LABELS[ra])
                .join(", ") || "No rolling averages"}
            </Typography>
          </Box>
        ))}
      </Box>

      {!breaksAlignment && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {!alreadyApplied && (
            <HPButton
              variant="outlined"
              color="primary"
              size="small"
              disabled={adoption.status === "blocked"}
              onClick={() =>
                adoption.status === "ready" && onApply(adoption.viewerOptions)
              }
            >
              Use tab time options
            </HPButton>
          )}

          {alreadyApplied && adoption.alignsDefault && (
            <Typography sx={{ fontSize: 11, color: "#667085" }}>
              This chart already uses these tab time options.
            </Typography>
          )}

          {adoption.status === "blocked" && (
            <Typography sx={{ fontSize: 11, color: "#667085" }}>
              Change this chart&apos;s saved {primaryNoun}
              {savedSetting ? ` (${labelFor(savedSetting)})` : ""} to one of the
              shared options before you can use these tab time options.
            </Typography>
          )}
          {adoption.status === "ready" && !adoption.alignsDefault && (
            <Typography sx={{ fontSize: 11, color: "#B54708" }}>
              The Tab Time Controls section will still show “Mixed configuration”
              until this chart&apos;s saved {primaryNoun} is also set to{" "}
              {labelFor(config.savedDefault)}.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TabTimeAlignmentCallout;
